import {
  QuestionAnsweredStatus,
  QuestionSearchSort,
  SearchQuestionsItemsQuery,
  SearchQuestionsTotalCountQuery,
} from '@/types/bff/gql-schema';
import { RealtimeQnaCardVo } from '@/types/qna';
import { absoluteDate, sortImageBlindTypeFormat } from '@/utils/format';
import { QueryClient } from '@tanstack/react-query';
import { useSearchQuestionsItemsQuery, useSearchQuestionsTotalCountQuery } from './qna.gql.impl';

interface Props {
  categoryIds: string[];
  page?: number;
  size?: number;
}

const DEFAULT_SIZE = 20;

const parseRealtimeQnaCardVo = (data: SearchQuestionsItemsQuery): RealtimeQnaCardVo[] => {
  const items = data.searchQuestions.items.map((item) => {
    return {
      answeredStatus: QuestionAnsweredStatus.Answered,
      answer: null,
      answererCount: 0,
      answererProfileImageUrls: [],
      question: {
        accessibleType: item.accessibleType,
        content: item.content,
        createdAt: item.createdAt,
        isBlockedByMe: item.isBlockedByMe,
        isLikedByMe: false,
        isMine: false,
        likeCount: 0,
        questionPid: item.publicId,
        title: item.title ?? '',
        attachments: sortImageBlindTypeFormat(
          item.attachments.map((attachment) => ({
            blindType: attachment.blindType,
            questionAttachmentId: attachment.id,
          }))
        ),
        category: {
          name: item.category.name,
          questionCategoryId: item.category.id,
          iconImageUrl: item.category.iconImagePath,
        },
        tags: item.tags,
        user: {
          name: item.userMaskedName,
          userId: item.userId,
        },
      },
      questionCreatedAt: absoluteDate(item.createdAt),
      answerCreatedAt: null,
      answeredStatusText: '',
    };
  });
  return items;
};

const parseRealtimeQnaCardTotalCount = (data: SearchQuestionsTotalCountQuery): number => {
  return data.searchQuestions.totalCount;
};

const useSearchQuestions = ({ categoryIds, page = 1, size = DEFAULT_SIZE }: Props) => {
  const request = {
    categoryIds,
    page,
    size,
    sort: QuestionSearchSort.Recent,
  };

  const { data: totalCount = 0, isLoading: isLoadingTotalCount } = useSearchQuestionsTotalCountQuery(
    {
      request: { ...request, page: 1 },
    },
    {
      staleTime: 5000,
      cacheTime: 5000,
      select: parseRealtimeQnaCardTotalCount,
    }
  );

  const { data: questions = [], isLoading } = useSearchQuestionsItemsQuery(
    {
      request,
    },
    {
      select: parseRealtimeQnaCardVo,
    }
  );

  const totalPage = Math.ceil(totalCount / size);

  return {
    totalCount,
    totalPage,
    isLoading: isLoadingTotalCount || isLoading,
    questions,
  };
};

export default useSearchQuestions;

useSearchQuestions.prefetchTotalCountQuery = async (queryClient: QueryClient, variables: Omit<Props, 'page'>) => {
  const request = {
    categoryIds: variables.categoryIds,
    page: 1,
    size: variables.size ?? DEFAULT_SIZE,
    sort: QuestionSearchSort.Recent,
  };

  await queryClient.prefetchQuery({
    queryKey: useSearchQuestionsTotalCountQuery.getKey({ request }),
    queryFn: () => useSearchQuestionsTotalCountQuery.fetcher({ request })(),
  });
};

useSearchQuestions.prefetchItemsQuery = async (queryClient: QueryClient, variables: Props) => {
  const request = {
    categoryIds: variables.categoryIds,
    page: variables.page ?? 1,
    size: variables.size ?? DEFAULT_SIZE,
    sort: QuestionSearchSort.Recent,
  };

  await queryClient.prefetchQuery({
    queryKey: useSearchQuestionsItemsQuery.getKey({ request }),
    queryFn: () =>
      useSearchQuestionsItemsQuery.fetcher({
        request,
      })(),
  });
};
